package org.moxultimate.topwithmox.libraries

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.milliseconds

/**
 * Demonstrates kotlinx.coroutines usage inside Kobweb Composables.
 *
 * Key APIs:
 * - [rememberCoroutineScope] — scope tied to the composable's lifecycle; auto-cancelled on removal.
 * - [LaunchedEffect] — side-effect that runs when a key changes; re-launched on key change.
 * - [kotlinx.coroutines.launch] — fire-and-forget coroutine from a button click.
 * - [kotlinx.coroutines.async] / [kotlinx.coroutines.Deferred.await] — parallel async work.
 * - [delay] — non-blocking suspend sleep; never use Thread.sleep() in JS.
 */
@Composable
fun CoroutinesSample() {
    val scope = rememberCoroutineScope()

    var autoStatus by remember { mutableStateOf("Waiting…") }
    var clickStatus by remember { mutableStateOf("Idle") }
    var parallelResult by remember { mutableStateOf("—") }

    // ── LaunchedEffect ────────────────────────────────────────────────────────
    // Runs once when the composable enters the composition.
    // Re-runs every time the key changes. Automatically cancelled on removal.
    LaunchedEffect(Unit) {
        autoStatus = "Starting…"
        delay(800L.milliseconds)
        autoStatus = "Ready ✓"
    }

    // ── rememberCoroutineScope ────────────────────────────────────────────────
    Column(Modifier.gap(1.cssRem)) {
        SpanText("LaunchedEffect: $autoStatus")
        SpanText("Click: $clickStatus")
        SpanText("Parallel: $parallelResult")

        // launch — starts a coroutine on click; does not block the UI
        Button(onClick = {
            scope.launch {
                clickStatus = "Loading…"
                delay(600L.milliseconds)         // suspend, not blocking
                clickStatus = "Done ✓"
            }
        }) { Text("Launch coroutine") }

        // async / await — run two tasks in parallel, combine results
        Button(onClick = {
            scope.launch {
                parallelResult = "Running…"
                val a = async { fetchFakeData("A", 500L) }
                val b = async { fetchFakeData("B", 300L) }
                parallelResult = "${a.await()} + ${b.await()}"   // waits for both
            }
        }) { Text("Parallel async/await") }
    }
}

/** Fake suspend function simulating a network call. */
private suspend fun fetchFakeData(label: String, delayMs: Long): String {
    delay(delayMs.milliseconds)
    return "Result-$label"
}
